/**
 * useStageGuard
 *
 * Stage guard for routes that sit BEFORE the irreversible data-collection /
 * analysis phase (`/sign`, `/connect`, `/confirm`).
 *
 * Why: the workflow stepper in `Layout.tsx` is intentionally non-clickable —
 * once an audit is in flight or finished, the user must stay on whichever
 * pipeline page corresponds to their backend state (jumping back to `/connect`
 * mid-analysis would break server-driven progress and confuse the UX). But
 * URL-bar typing, bookmarks, and browser back/forward can still drop the user
 * on an earlier route. This hook reconciles that by reading the user's actual
 * workflow stage from Supabase and `navigate(..., { replace: true })`-ing
 * forward when needed.
 *
 * Policy: redirect FORWARD into the irreversible part of the pipeline
 * (`/processing/...`, `/generate-report/...`, `/report/...`). Movement between
 * `/sign`, `/connect`, and `/confirm` stays free; those edits are safe.
 */
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/useAuthStore';
import { resolvePostLoginRoute } from '../utils/postLoginRoute';

const IRREVERSIBLE_PREFIXES = ['/processing', '/generate-report', '/report'] as const;

function isIrreversible(path: string): boolean {
  return IRREVERSIBLE_PREFIXES.some((p) => path === p || path.startsWith(`${p}/`));
}

/**
 * Resolves the user's current workflow stage and redirects FORWARD if the
 * resolved route is past the data-collection phase. Skips entirely for
 * unauthenticated users (they have no workflow snapshot to read) and for
 * employees (they're not in the lead workflow).
 *
 * Pass the route this hook is being called from (used to avoid no-op
 * navigation and to prevent self-redirect loops).
 *
 * Returns the verdict so a screen with a SIDE EFFECT can wait for it. Most
 * callers can ignore it — they only render — but `/collect` kicks off an agent
 * run on mount, and starting that before the guard has spoken would fire a
 * fresh pass (API calls, new source_data) for a user whose audit is already
 * running or finished. `'checking'` means "not decided yet"; only `'clear'`
 * means "this screen may act".
 */
export type StageVerdict = 'checking' | 'clear' | 'redirecting';

export function useStageGuard(currentRoute: string): StageVerdict {
  const navigate = useNavigate();
  const user = useAuthStore((s) => s.user);
  const userRole = useAuthStore((s) => s.userRole);
  const loading = useAuthStore((s) => s.loading);
  // Run at most once per mount — `resolvePostLoginRoute` hits Supabase and the
  // audit-status endpoint, so we don't want to fire it repeatedly as auth
  // store fields settle.
  const ranRef = useRef(false);
  const [verdict, setVerdict] = useState<StageVerdict>('checking');

  useEffect(() => {
    if (ranRef.current) return;
    if (loading) return;
    if (!user?.id) return;
    // Staff aren't in the lead workflow, so nothing gates them. Derived at
    // the return below rather than stored — it's known at render time, and
    // setting state from the effect body only invites a cascading render.
    if (userRole === 'employee') return;
    ranRef.current = true;

    let cancelled = false;
    (async () => {
      try {
        const resolved = await resolvePostLoginRoute(user.id, {
          isEmployee: userRole === 'employee',
        });
        if (cancelled) return;
        // Editing transitions within the pre-audit flow are user-initiated and
        // allowed; only a real pipeline state forces navigation.
        if (resolved !== currentRoute && isIrreversible(resolved)) {
          setVerdict('redirecting');
          navigate(resolved, { replace: true });
          return;
        }
        setVerdict('clear');
      } catch {
        /* never block render on a failed RPC — the user can still use the page.
           Clearing here is deliberate: a transient RPC failure must not leave a
           screen that waits on the verdict stuck forever. */
        if (!cancelled) setVerdict('clear');
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [user?.id, userRole, loading, currentRoute, navigate]);

  return userRole === 'employee' ? 'clear' : verdict;
}
